import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAppAuth } from "./MockAuthProvider";
import { record, identifyUser } from "aws-amplify/analytics";
import ChatCoachAI from "./pages/ChatCoachAI";
import ScenarioPractice from "./pages/ScenarioPractice";
import { ChatProvider } from "./context/ChatContext";
import { ScenarioProvider } from "./context/ScenarioContext";
import { currentConfig, LOCAL_DEV_MODE } from "./config";

/**
 * Drop every cached OIDC artefact so a failed sign-in can be retried cleanly.
 *
 * oidc-client-ts keeps these in two different places, both under an "oidc."
 * prefix: the signed-in user in sessionStorage (`userStore`) and the in-flight
 * signin state — PKCE verifier and nonce — in localStorage (`stateStore`).
 *
 * This used to remove a single hardcoded
 * `oidc.user:{authority}:{client_id}` key from localStorage, which never
 * matched anything: that key belongs to sessionStorage, and the authority it
 * named was the Beta user pool, so it was wrong about both the store and the
 * pool. The leftover localStorage state is the part that actually wedges a
 * retry, and nothing was clearing it.
 *
 * localStorage is filtered by prefix rather than cleared outright so the saved
 * theme survives, and iterated backwards because removeItem reindexes.
 */
export const clearStoredAuthState = () => {
  sessionStorage.clear();
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i);
    if (key && key.indexOf("oidc.") === 0) {
      localStorage.removeItem(key);
    }
  }
};

const LoginPage = () => {
  const auth = useAppAuth();

  useEffect(() => {
    if (auth.isAuthenticated) {
      window.location.href = currentConfig.redirectUri;
    }
  }, [auth.isAuthenticated]);

  if (auth.isLoading)
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  if (auth.error) {
    // Clear problematic state on error
    const clearAndRetry = () => {
      clearStoredAuthState();
      window.location.reload();
    };
    
    return (
      <div className="flex flex-col justify-center items-center h-screen gap-4">
        <div className="text-red-600 font-bold">Authentication Error</div>
        <div className="max-w-md text-center">{auth.error.message}</div>
        <button
          onClick={clearAndRetry}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mt-4"
        >
          Clear Session & Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center h-screen gap-4 text-foreground relative">
      {/* Background gradient */}
      <div className="background-gradient"></div>
      <h1 className="text-2xl font-bold">Welcome to CoachAI</h1>
      <button
        onClick={() => auth.signinRedirect()}
        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
      >
        Sign in
      </button>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const auth = useAppAuth();
  if (auth.isLoading)
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  if (!auth.isAuthenticated) return <Navigate to="/login" />;
  return children;
};

function App() {
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark"
  );
  const auth = useAppAuth();
  const location = useLocation();
  const sessionStartTimeRef = useRef(Date.now());

  // — New: identify user in Pinpoint by email once authenticated (skip in local dev)
  useEffect(() => {
    if (LOCAL_DEV_MODE) {
      console.log('🔧 [LOCAL DEV MODE] Analytics disabled for local testing');
      return;
    }
    if (!auth.isAuthenticated) return;
    const email = auth.user?.profile?.email;
    if (email) {
      identifyUser({
        userId: email,
        userProfile: { email },
        options: { userAttributes: { email: [email] } },
      });
    }
  }, [auth.isAuthenticated]);

  // Redirect after login and handle errors
  useEffect(() => {
    if (auth.isAuthenticated && window.location.pathname === "/login") {
      window.location.href = currentConfig.redirectUri;
    }
    
    // Handle authentication errors
    if (auth.error) {
      console.error("Auth error:", auth.error);
      // Clear any problematic state
      clearStoredAuthState();
      // Redirect to login after clearing state
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
  }, [auth.isAuthenticated, auth.error]);

  // Dark mode
  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  // Page views (skip in local dev)
  useEffect(() => {
    if (LOCAL_DEV_MODE) {
      console.log(`🔧 [LOCAL DEV MODE] Page view analytics disabled: ${location.pathname}`);
      return;
    }
    record({ name: "page_view", attributes: { page: location.pathname } });
  }, [location.pathname]);

  // Session start/end (skip in local dev)
  useEffect(() => {
    if (LOCAL_DEV_MODE) {
      console.log('🔧 [LOCAL DEV MODE] Session analytics disabled for local testing');
      return;
    }
    sessionStartTimeRef.current = Date.now();
    record({ name: "session_start" });
    const onUnload = () => {
      const duration = Math.round(
        (Date.now() - sessionStartTimeRef.current) / 1000
      );
      record({
        name: "session_end",
        attributes: { duration: duration.toString() },
        metrics: { sessionDuration: duration },
      });
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      onUnload();
    };
  }, []);

  const signOutRedirect = () => {
    const clientId = "2elbk24054arsa4mg1ndlap9o7";
    const logoutUri = currentConfig.logoutUri;
    const cognitoDomain =
      "https://us-east-1wqdymj50t.auth.us-east-1.amazoncognito.com";
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri
    )}`;
  };

  return (
    <ChatProvider>
      <ScenarioProvider>
        <div className="flex h-screen text-foreground transition-colors duration-300 relative">
          {/* Background gradient */}
          <div className="background-gradient"></div>
          <div className="flex-1 flex flex-col overflow-hidden">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <ChatCoachAI />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scenario"
                element={
                  <ProtectedRoute>
                    <ScenarioPractice />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </div>
      </ScenarioProvider>
    </ChatProvider>
  );
}

export default App;
